import { SCALAR_FIELDS } from "@/lib/graphql/serialize-input";
import { anchorBareCalendarDate } from "@/lib/domain/calendar-date-anchor";
import { asCalendarDate } from "@/lib/domain/calendar-date-brand";
import type {
  WithCalendarDateFieldsIfPresent,
  WithInstantFieldsIfPresent,
} from "@/lib/domain/calendar-date-branded-writes";

/**
 * Canonical list of deal `timestamptz` fields the shared calendar date editor
 * can emit as a bare "YYYY-MM-DD" — kept in sync with the registry so a future
 * addition to `SCALAR_FIELDS.deals.datetime` is anchored automatically, the
 * same way `decimal-fields.ts` derives `DECIMAL_FIELDS`. Currently
 * `won_at`/`lost_at`; `close_date` is a true Date scalar and is intentionally
 * absent from that registry entry (see `lib/graphql/serialize-input.ts`) —
 * it is validated separately, below, since it needs no UTC-midnight widening.
 */
const DATETIME_FIELDS = SCALAR_FIELDS.deals.datetime;

/**
 * The one true `date` scalar this same shared calendar editor can emit.
 * Unlike `DATETIME_FIELDS`, this is NOT anchored (a `date` column has no
 * timezone to get wrong) — it is only VALIDATED, so a value that is not a
 * real bare "YYYY-MM-DD" fails loudly here instead of reaching Postgres as
 * an unvalidated string (which a full instant would silently truncate).
 */
const DATE_ONLY_FIELDS = ["close_date"] as const;

/**
 * Anchor a bare `won_at`/`lost_at` calendar date at noon `America/Chicago`
 * BEFORE the input reaches `normalizeGraphqlDatetimeInput`'s UTC-midnight
 * widening — the same defect class as #2520 (`contact_tasks.due_date`) and
 * #2545 (`tickets.due_date`), reachable here through two client write paths:
 * the deal detail-modal's inline calendar editor (`CalendarDateEdit`, via
 * `useDealHooks().useUpdate` → `prepareDealMutationInput`) and the Create Deal
 * form's "scheduling" section (`EnhancedDealsConfig.ts`, via `useCreateDeal`/
 * `useUpdateDeal`). Both emit a date-only string for these two fields; see
 * `lib/domain/calendar-date-anchor.ts` for why noon (not midnight) is the fix.
 *
 * An already-precise instant (a caller who computed one deliberately — e.g. a
 * server-stamped mark-won/mark-lost timestamp routed through this same input
 * prep) and null/absent values pass through `anchorBareCalendarDate`
 * untouched. Fields not present on the input are left alone.
 *
 * Also validates (not anchors — see `DATE_ONLY_FIELDS`'s own comment)
 * `close_date` when present, so the same widened branded return type this
 * function's callers declare (`BrandedDealsInsertInput`/
 * `BrandedDealsUpdateInput` in `mutations.ts`) is actually satisfied rather
 * than requiring a second, separate validation step at every call site.
 *
 * Return type: `WithCalendarDateFieldsIfPresent<WithInstantFieldsIfPresent<T,
 * "won_at" | "lost_at">, "close_date">`, not plain `T` — `won_at`/`lost_at`
 * come back branded `Instant` and `close_date` comes back branded
 * `CalendarDate` (`calendar-date-brand.ts`) when `T` statically has those
 * fields (a `Record<string, unknown>` caller — the test suite calls this
 * generically — degrades to no branding rather than an error; see
 * `calendar-date-branded-writes.ts`). This is this function's OWN
 * postcondition (it is one of the four sanctioned anchor primitives,
 * `ANCHOR_FN_NAMES` in the write-side scanner), so the final `as` here brands
 * the function's actual guarantee rather than laundering a brand past a
 * caller.
 */
export function anchorDealCalendarDates<T extends object>(
  input: T
): WithCalendarDateFieldsIfPresent<WithInstantFieldsIfPresent<T, "won_at" | "lost_at">, "close_date"> {
  const record = input as Record<string, unknown>;
  const out: Record<string, unknown> = { ...record };

  for (const field of DATETIME_FIELDS) {
    if (!(field in record)) continue;
    const value = record[field];
    if (value === null || value === undefined || typeof value === "string") {
      out[field] = anchorBareCalendarDate(value);
    }
  }

  for (const field of DATE_ONLY_FIELDS) {
    if (!(field in record)) continue;
    const value = record[field];
    if (value === null || value === undefined) continue;
    if (typeof value === "string") out[field] = asCalendarDate(value);
  }

  return out as WithCalendarDateFieldsIfPresent<
    WithInstantFieldsIfPresent<T, "won_at" | "lost_at">,
    "close_date"
  >;
}
